import { useState, useRef, useCallback, useEffect } from "react";
import {
  Alert,
  Box,
  Typography,
  Collapse,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useAppSelector } from "../../../redux/store";
import { PageHeader } from "../../../ui";

import {
  WELCOME,
  SECTIONS,
  FAQ_CATEGORIES,
  type Section,
  type SubSection,
  type FAQCategory,
  type FAQ,
} from "./content";

// ─── Collapsible Sub-Section ────────────────────────────────────────
const CollapsibleSubSection = ({
  sub,
  defaultOpen = false,
}: {
  sub: SubSection;
  defaultOpen?: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box
      sx={{
        backgroundColor: open
          ? "var(--surface-container-lowest, #ffffff)"
          : "transparent",
        borderRadius: 0,
        mb: 1,
        transition: "background-color 0.25s ease",
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
          px: 2.5,
          cursor: "pointer",
          borderRadius: 0,
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: open
              ? "var(--surface-container-lowest, #ffffff)"
              : "rgba(255, 255, 255, 0.5)",
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Brandon Grotesque", sans-serif',
            fontWeight: open ? 700 : 500,
            fontSize: "0.85rem",
            color: open ? "var(--on-surface)" : "var(--on-surface-variant)",
            transition: "all 0.2s ease",
          }}
        >
          {sub.title}
        </Typography>
        <KeyboardArrowRightIcon
          sx={{
            fontSize: "1rem",
            color: "var(--outline)",
            transition: "transform 0.25s ease",
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            flexShrink: 0,
            ml: 1,
          }}
        />
      </Box>
      <Collapse in={open} timeout={300}>
        <Box sx={{ pb: 3, px: 2.5, pt: 0.5 }}>
          {sub.body.split("\n\n").map((paragraph, i) => (
            <Typography
              key={i}
              sx={{
                fontFamily: '"Brandon Grotesque", sans-serif',
                fontSize: "0.82rem",
                lineHeight: 1.9,
                color: "var(--on-surface-variant)",
                mb: i < sub.body.split("\n\n").length - 1 ? 2.5 : 0,
              }}
            >
              {paragraph}
            </Typography>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Major Section Block ────────────────────────────────────────────
const SectionBlock = ({
  section,
  isActive,
}: {
  section: Section;
  isActive: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      sx={{
        backgroundColor: "var(--surface-container-lowest, #ffffff)",
        borderRadius: 0,
        overflow: "hidden",
        border: isActive
          ? "1px solid var(--tpc-600)"
          : "1px solid var(--tpc-200)",
        transition: "border-color 0.2s ease",
      }}
    >
      {/* Section header */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          px: { xs: 3, md: 4 },
          py: { xs: 3.5, md: 4 },
          cursor: "pointer",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.008)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 3,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: '"Brandon Grotesque", sans-serif',
                fontWeight: 700,
                fontSize: { xs: "1.1rem", md: "1.3rem" },
                color: "var(--on-surface)",
                mb: 1.5,
              }}
            >
              {section.title}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Brandon Grotesque", sans-serif',
                fontSize: "0.82rem",
                lineHeight: 1.75,
                color: "var(--on-surface-variant)",
                maxWidth: 560,
              }}
            >
              {section.summary}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
              pt: 0.5,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Brandon Grotesque", sans-serif',
                fontSize: "0.55rem",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "var(--outline)",
                display: { xs: "none", sm: "block" },
              }}
            >
              {section.subsections.length} topics
            </Typography>
            <IconButton
              size="small"
              sx={{
                color: "var(--outline)",
                transition: "transform 0.3s ease",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Expanded subsections */}
      <Collapse in={expanded} timeout={400}>
        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            pb: 2.5,
            pt: 1.5,
            backgroundColor: "var(--surface-container-low, #f7f7f7)",
            mx: { xs: 2, md: 3 },
            mb: { xs: 2.5, md: 3 },
            borderRadius: 0,
          }}
        >
          {section.subsections.map((sub) => (
            <CollapsibleSubSection
              key={sub.title}
              sub={sub}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── FAQ Item ───────────────────────────────────────────────────────
const FAQItem = ({ faq }: { faq: FAQ }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box
      sx={{
        backgroundColor: open
          ? "var(--surface-container-lowest, #ffffff)"
          : "transparent",
        borderRadius: 0,
        mb: 0.5,
        transition: "background-color 0.25s ease",
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 2,
          px: 2.5,
          cursor: "pointer",
          borderRadius: 0,
          gap: 2,
          "&:hover": {
            backgroundColor: open
              ? "var(--surface-container-lowest, #ffffff)"
              : "rgba(255,255,255,0.5)",
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Brandon Grotesque", sans-serif',
            fontWeight: open ? 600 : 500,
            fontSize: "0.82rem",
            color: open ? "var(--on-surface)" : "var(--on-surface-variant)",
            transition: "all 0.2s ease",
            flex: 1,
          }}
        >
          {faq.question}
        </Typography>
        <KeyboardArrowRightIcon
          sx={{
            fontSize: "0.9rem",
            color: "var(--outline)",
            transition: "transform 0.2s ease",
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            flexShrink: 0,
          }}
        />
      </Box>
      <Collapse in={open} timeout={250}>
        <Typography
          sx={{
            fontFamily: '"Brandon Grotesque", sans-serif',
            fontSize: "0.8rem",
            lineHeight: 1.85,
            color: "var(--on-surface-variant)",
            pb: 2.5,
            px: 2.5,
          }}
        >
          {faq.answer}
        </Typography>
      </Collapse>
    </Box>
  );
};

// ─── FAQ Category Group ─────────────────────────────────────────────
const FAQSection = ({ category }: { category: FAQCategory }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      sx={{
        backgroundColor: expanded
          ? "var(--surface-container-lowest, #ffffff)"
          : "transparent",
        borderRadius: 0,
        mb: 1,
        transition: "all 0.25s ease",

      }}
    >
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          px: 3,
          py: 2.5,
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: 0,
          "&:hover": {
            backgroundColor: expanded
              ? "var(--surface-container-lowest, #ffffff)"
              : "rgba(255,255,255,0.5)",
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Brandon Grotesque", sans-serif',
            fontWeight: 700,
            fontSize: "0.85rem",
            color: "var(--on-surface)",
          }}
        >
          {category.title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontFamily: '"Brandon Grotesque", sans-serif',
              fontSize: "0.5rem",
              letterSpacing: "0.1em",
              color: "var(--outline)",
            }}
          >
            {category.items.length}
          </Typography>
          <ExpandMoreIcon
            sx={{
              fontSize: "1rem",
              color: "var(--outline)",
              transition: "transform 0.3s ease",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </Box>
      </Box>
      <Collapse in={expanded} timeout={300}>
        <Box sx={{ px: 1, pb: 2 }}>
          {category.items.map((faq) => (
            <FAQItem key={faq.question} faq={faq} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Sticky Table of Contents ───────────────────────────────────────
const TableOfContents = ({
  activeSection,
  onNavigate,
}: {
  activeSection: string;
  onNavigate: (id: string) => void;
}) => {
  const allNavItems = [
    { id: "welcome", label: "Welcome" },
    ...SECTIONS.map((s) => ({ id: s.id, label: s.title })),
    { id: "faqs", label: "FAQs" },
  ];

  return (
    <Box
      sx={{
        position: "sticky",
        top: 80,
        display: { xs: "none", lg: "block" },
        width: 190,
        flexShrink: 0,
        pt: 6,
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Brandon Grotesque", sans-serif',
          fontSize: "0.45rem",
          letterSpacing: "0.3em",
          textTransform: "uppercase",
          color: "var(--outline)",
          mb: 2.5,
          px: 1.5,
        }}
      >
        In This Guide
      </Typography>
      {allNavItems.map((item) => (
        <Box
          key={item.id}
          onClick={() => onNavigate(item.id)}
          sx={{
            px: 1.5,
            py: 1,
            cursor: "pointer",
            transition: "all 0.2s ease",
            borderLeft:
              activeSection === item.id
                ? "2px solid var(--on-surface)"
                : "2px solid transparent",
            "&:hover": {
              borderLeftColor: "var(--outline)",
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Brandon Grotesque", sans-serif',
              fontWeight: activeSection === item.id ? 700 : 500,
              fontSize: "0.65rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color:
                activeSection === item.id
                  ? "var(--on-surface)"
                  : "var(--outline)",
              transition: "all 0.2s ease",
            }}
          >
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════
export default function WhatToExpect() {
  const [activeSection, setActiveSection] = useState("welcome");
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const { user } = useAppSelector((state) => state.login);
  const { projects, selectedProject } = useAppSelector(
    (state) => state.projects
  );
  const firstName = user?.username?.split(" ")[0] || "";
  const project: any = selectedProject || projects?.[0] || null;
  const businesses = Array.isArray(project?.doingBusinessWith)
    ? project.doingBusinessWith.filter(Boolean)
    : [];
  const businessLabel = businesses.join(" · ") || "Pinnacle Mountain Homes";
  const isPinnacleMountainHomes =
    businesses.length === 0 ||
    businesses.some((value: string) => /pinnacle mountain|pmh/i.test(value));

  // Scroll-spy
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-100px 0px -60% 0px", threshold: 0.1 }
    );

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleNavigate = useCallback((id: string) => {
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const registerRef = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      sectionRefs.current[id] = el;
    },
    []
  );

  return (
    <Box
      sx={{
        maxWidth: 1100,
        mx: "auto",
        px: { xs: 2, md: 0 },
        pb: 8,
        display: "flex",
        gap: 5,
        alignItems: "flex-start",
      }}
    >
      {/* Sticky side navigation */}
      <TableOfContents
        activeSection={activeSection}
        onNavigate={handleNavigate}
      />

      {/* Main content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <PageHeader
          eyebrow={`Your guide · ${businessLabel}`}
          title={`What to expect${firstName ? `, ${firstName}` : ""}`}
          summary="From groundbreaking to move-in: how the project runs, who to call, and what happens next."
        />

        {!isPinnacleMountainHomes && (
          <Alert severity="info" sx={{ borderRadius: 0, mb: 3 }}>
            This guide is written for Pinnacle Mountain Homes. Your project is
            with {businessLabel}; the {businessLabel} version is being prepared
            and your project team will share it.
          </Alert>
        )}

        {/* ── Welcome Section ── */}
        <Box
          ref={registerRef("welcome")}
          id="welcome"
          sx={{
            mb: 6,
            backgroundColor: "var(--surface-container-low, #f7f7f7)",
            borderRadius: 0,
            p: { xs: 3, md: 4.5 },
            scrollMarginTop: "80px",
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Brandon Grotesque", sans-serif',
              fontWeight: 700,
              fontSize: "1.15rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--on-surface)",
              mb: 2.5,
            }}
          >
            {WELCOME.headline}
          </Typography>

          {WELCOME.body.split("\n\n").map((p, i) => (
            <Typography
              key={i}
              sx={{
                fontFamily: '"Brandon Grotesque", sans-serif',
                fontSize: "0.85rem",
                lineHeight: 1.9,
                color: "var(--on-surface-variant)",
                mb: 2,
              }}
            >
              {p}
            </Typography>
          ))}

          {/* Promise cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mt: 4,
              mb: 4,
            }}
          >
            {WELCOME.promises.map((p) => (
              <Box
                key={p.label}
                sx={{
                  backgroundColor:
                    "var(--surface-container-lowest, #ffffff)",
                  borderRadius: 0,
                  p: 3,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Brandon Grotesque", sans-serif',
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    color: "var(--on-surface)",
                    mb: 1,
                  }}
                >
                  {p.label}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Brandon Grotesque", sans-serif',
                    fontSize: "0.75rem",
                    lineHeight: 1.75,
                    color: "var(--on-surface-variant)",
                  }}
                >
                  {p.detail}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography
            sx={{
              fontFamily: '"Brandon Grotesque", sans-serif',
              fontSize: "0.85rem",
              lineHeight: 1.9,
              color: "var(--on-surface-variant)",
              fontStyle: "italic",
            }}
          >
            {WELCOME.closing}
          </Typography>
        </Box>

        {/* ── Main Sections ── */}
        {SECTIONS.map((section) => (
          <Box
            key={section.id}
            ref={registerRef(section.id)}
            id={section.id}
            sx={{ mb: 5, scrollMarginTop: "80px" }}
          >
            <SectionBlock
              section={section}
              isActive={activeSection === section.id}
            />
          </Box>
        ))}

        {/* ── FAQs ── */}
        <Box
          ref={registerRef("faqs")}
          id="faqs"
          sx={{ mt: 4, mb: 6, scrollMarginTop: "80px" }}
        >
          <Typography
            sx={{
              fontFamily: '"Brandon Grotesque", sans-serif',
              fontSize: "0.55rem",
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              color: "var(--outline)",
              mb: 1.5,
            }}
          >
            Common Questions
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Brandon Grotesque", sans-serif',
              fontWeight: 700,
              fontSize: { xs: "1.25rem", md: "1.5rem" },
              color: "var(--on-surface)",
              mb: 1.5,
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Brandon Grotesque", sans-serif',
              fontSize: "0.82rem",
              color: "var(--on-surface-variant)",
              mb: 4,
              maxWidth: 520,
              lineHeight: 1.75,
            }}
          >
            We know that building a custom home comes with a lot of
            questions — and we love that! Here are answers to the most
            common ones.
          </Typography>

          <Box
            sx={{
              backgroundColor: "var(--surface-container-low, #f7f7f7)",
              borderRadius: 0,
              p: { xs: 2, md: 2.5 },
            }}
          >
            {FAQ_CATEGORIES.map((cat) => (
              <FAQSection key={cat.title} category={cat} />
            ))}
          </Box>
        </Box>

        {/* ── Closing CTA ── */}
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 4,
            backgroundColor: "var(--surface-container-low, #f7f7f7)",
            borderRadius: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Brandon Grotesque", sans-serif',
              fontWeight: 700,
              fontSize: "1.25rem",
              color: "var(--on-surface)",
              mb: 1.5,
            }}
          >
            Questions Not Covered Here?
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Brandon Grotesque", sans-serif',
              fontSize: "0.85rem",
              color: "var(--on-surface-variant)",
              lineHeight: 1.75,
              maxWidth: 480,
              mx: "auto",
            }}
          >
            Don't hesitate to reach out — we're here to help! Your
            Project Manager is always available to guide you every step
            of the way.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
